import { useEffect, useCallback, useRef } from "react";
import { useAttendeesStore } from "../store/attendeesStore";
import { useResourceStore } from "../store/resourceStore";
import { useOutlookStore } from "../store/outlookStore";
import { ExchangeResourceTypeEnum } from "../hooks/useOutlookActions";

/**
 * Simplified Office Events Hook - handles bidirectional sync between Outlook and stores
 * Optimized for performance and reusability
 */
export const useOfficeEvents = () => {
  const { setRequiredAttendees, setOptionalAttendees } = useAttendeesStore();
  const { selectedResource, setSelectedResource, setAvailableResource, availableResource } = useResourceStore();
  const { requiredAttendees, optionalAttendees, enhancedLocation } = useOutlookStore();

  // Sync flags to prevent infinite loops
  const syncInProgress = useRef(false);
  const lastSyncData = useRef({
    requiredAttendees: "",
    optionalAttendees: "",
    enhancedLocation: "",
  });

  // ✅ UNIFIED SYNC - Handles ALL resource types in one place (prevents race conditions)
  const syncAllResources = useCallback(() => {
    if (syncInProgress.current) return;

    const requiredData = JSON.stringify(requiredAttendees);
    const optionalData = JSON.stringify(optionalAttendees);
    const locationData = JSON.stringify(enhancedLocation);

    // Only update if ANY data changed
    const dataChanged =
      requiredData !== lastSyncData.current.requiredAttendees ||
      optionalData !== lastSyncData.current.optionalAttendees ||
      locationData !== lastSyncData.current.enhancedLocation;

    if (!dataChanged) return;

    console.log("🔄 UNIFIED SYNC: Processing all Outlook data changes:", {
      requiredAttendees: requiredAttendees.length,
      optionalAttendees: optionalAttendees.length,
      enhancedLocations: enhancedLocation.length,
    });

    syncInProgress.current = true;

    try {
      // 1. Sync to attendees store
      setRequiredAttendees(requiredAttendees);
      setOptionalAttendees(optionalAttendees);

      // 2. Build comprehensive outlook resource list
      const allAttendeeEmails = new Set([
        ...requiredAttendees.map((att) => att.Email.toLowerCase()),
        ...optionalAttendees.map((att) => att.Email.toLowerCase()),
      ]);

      const outlookLocationEmails = new Set(enhancedLocation.map((loc) => (loc.emailAddress || "").toLowerCase()));

      // 3. Convert Outlook locations to Room resources
      const outlookRoomResources = enhancedLocation.map((location) => ({
        ID: undefined,
        Name: location.displayName || location.emailAddress || "Unknown Resource",
        Email: location.emailAddress || "",
        ResourceType: ExchangeResourceTypeEnum.Room,
        RecipientType: ExchangeResourceTypeEnum.Room,
        Location: location.displayName || "",
        Description: `Resource: ${location.displayName}`,
        Capacity: 0,
        IsAvailable: true,
      }));

      // 4. Determine what resources should remain selected
      const currentlySelectedEmails = new Set(selectedResource.map((r) => r.Email.toLowerCase()));

      // Keep Equipment resources that are still in attendees
      const keepEquipmentResources = selectedResource.filter(
        (resource) =>
          resource.ResourceType === ExchangeResourceTypeEnum.Equipment &&
          allAttendeeEmails.has(resource.Email.toLowerCase())
      );

      // Keep Room resources that are still in enhanced locations
      const keepRoomResources = selectedResource.filter(
        (resource) =>
          resource.ResourceType === ExchangeResourceTypeEnum.Room &&
          outlookLocationEmails.has(resource.Email.toLowerCase())
      );

      // Add new Room resources from Outlook
      const newRoomResources = outlookRoomResources.filter(
        (resource) => !currentlySelectedEmails.has(resource.Email.toLowerCase())
      );

      // 5. Build final selected resources list (SINGLE SOURCE OF TRUTH)
      const finalSelectedResources = [...keepEquipmentResources, ...keepRoomResources, ...newRoomResources];

      // 6. Find resources that were removed (for moving back to available)
      const removedResources = selectedResource.filter((resource) => {
        const email = resource.Email.toLowerCase();

        if (resource.ResourceType === ExchangeResourceTypeEnum.Equipment) {
          return !allAttendeeEmails.has(email);
        } else if (resource.ResourceType === ExchangeResourceTypeEnum.Room) {
          return !outlookLocationEmails.has(email);
        }
        return false;
      });

      // 7. Update available resources (add back removed ones, avoid duplicates)
      let newAvailableResources = availableResource;
      if (removedResources.length > 0) {
        console.log(
          "🔄 Moving resources back to available:",
          removedResources.map((r) => r.Name)
        );

        const resourcesToAddBack = removedResources.filter(
          (resource) =>
            !availableResource.some((available) => available.Email.toLowerCase() === resource.Email.toLowerCase())
        );

        if (resourcesToAddBack.length > 0) {
          newAvailableResources = [...availableResource, ...resourcesToAddBack];
        }
      }

      // 8. Single atomic update (prevents race conditions)
      setSelectedResource(finalSelectedResources);
      if (newAvailableResources !== availableResource) {
        setAvailableResource(newAvailableResources);
      }

      // 9. Update tracking data
      lastSyncData.current.requiredAttendees = requiredData;
      lastSyncData.current.optionalAttendees = optionalData;
      lastSyncData.current.enhancedLocation = locationData;

      console.log("✅ UNIFIED SYNC COMPLETE:", {
        selectedResources: finalSelectedResources.length,
        availableResources: newAvailableResources.length,
        removedResources: removedResources.length,
      });
    } catch (error) {
      console.error("❌ Error in unified sync:", error);
    } finally {
      syncInProgress.current = false;
    }
  }, [
    requiredAttendees,
    optionalAttendees,
    enhancedLocation,
    selectedResource,
    availableResource,
    setRequiredAttendees,
    setOptionalAttendees,
    setSelectedResource,
    setAvailableResource,
  ]);

  // ✅ Main sync function for manual triggers
  const triggerSync = useCallback(() => {
    console.log("🔄 Manual sync triggered");
    syncAllResources();
  }, [syncAllResources]);

  // ✅ Auto sync when ANY Outlook data changes (unified approach)
  useEffect(() => {
    syncAllResources();
  }, [syncAllResources]);

  // ✅ Cleanup
  useEffect(() => {
    return () => {
      syncInProgress.current = false;
    };
  }, []);

  return {
    // Status
    isInitialized: true,

    // Manual sync
    triggerSync,

    // Sync status for preventing conflicts
    isSyncInProgress: () => syncInProgress.current,

    // Data counts for debugging
    counts: {
      requiredAttendees: requiredAttendees.length,
      optionalAttendees: optionalAttendees.length,
      enhancedLocation: enhancedLocation.length,
      selectedResources: selectedResource.length,
    },
  };
};
